"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var immer_1 = require("immer");
var Filters_1 = require("../types/Filters");
var isNull_1 = require("../utils/isNull");
var isUndefined_1 = require("../utils/isUndefined");
function updateFacets(baseState, updatedFilter) {
    // eslint-disable-next-line consistent-return
    var next = immer_1.produce(baseState, function (draft) {
        var _a, _b;
        var parentKey = Object.keys(baseState).find(function (key) { var _a; return ((_a = baseState[key]) === null || _a === void 0 ? void 0 : _a.id) === updatedFilter.parentId; });
        if (!parentKey) {
            return draft;
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        var group = draft[parentKey];
        if (Filters_1.isRangeFilter(updatedFilter) && Filters_1.isRangeFilter(group.filters)) {
            group.filters = updatedFilter;
            // eslint-disable-next-line no-param-reassign
            draft = __assign(__assign({}, draft), (_a = {}, _a[parentKey] = group, _a));
        }
        else {
            var indexToUpdate = group.filters.findIndex(function (cbf) { return cbf.id === updatedFilter.id; });
            group.filters[indexToUpdate] = updatedFilter;
            // eslint-disable-next-line no-param-reassign
            draft = __assign(__assign({}, draft), (_b = {}, _b[parentKey] = group, _b));
        }
    });
    return next;
}
function mergeFacets(newFacets, activeFilters) {
    var next = immer_1.produce(newFacets, function (draft) {
        var _loop_1 = function (i) {
            var _a, _b;
            var filter = activeFilters[i];
            var parentKey = Object.keys(newFacets).find(function (key) { var _a; return ((_a = newFacets[key]) === null || _a === void 0 ? void 0 : _a.id) === filter.parentId; });
            if (isUndefined_1.isUndefined(parentKey)) {
                throw new Error("Failed to merge facets, a facet (" + filter.parentId.toString() + ") that was in the previous object does not exist in the new object");
            }
            var facet = draft[parentKey];
            if (Filters_1.isRangeFilter(filter)) {
                facet.filters = filter;
                draft = __assign(__assign({}, draft), (_a = {}, _a[parentKey] = facet, _a));
            }
            else {
                var indexToUpdate = facet.filters.findIndex(function (cbf) { return cbf.id === filter.id; });
                if (indexToUpdate === -1) {
                    facet.filters.push(filter);
                }
                else {
                    facet.filters[indexToUpdate] = filter;
                }
                draft = __assign(__assign({}, draft), (_b = {}, _b[parentKey] = facet, _b));
            }
        };
        for (var i = 0; i < activeFilters.length; i++) {
            _loop_1(i);
        }
    });
    return next;
}
function updateActiveFilters(baseState, updatedFilter) {
    var next = immer_1.produce(baseState, function (draft) {
        var index = baseState.findIndex(function (activeFilter) { return activeFilter.id === updatedFilter.id; });
        if (index === -1) {
            draft.push(updatedFilter);
        }
        else {
            if (Filters_1.isRangeFilter(updatedFilter)) {
                if (isNull_1.isNull(updatedFilter.min) && isNull_1.isNull(updatedFilter.max)) {
                    // the range filter was already in the active filters array, but now it is
                    // inactive as both min and max are null, therefore we need to remove it.
                    draft.splice(index, 1);
                }
                else {
                    // the range filter is still considered active as it has either/both min/max value(s),
                    // in this instance one/both of them have changed value, so simply update with the latest values.
                    draft[index] = updatedFilter;
                }
            }
            else {
                // If the checkbox filter is already in the activefilters array, and has been unchecked,
                // then we can simply remove it from the list
                draft.splice(index, 1);
            }
        }
        return draft;
    });
    return next;
}
function clearFilters(facets) {
    var next = immer_1.produce(facets, function (draft) {
        var keys = Object.keys(draft).filter(function (k) { return !isUndefined_1.isUndefined(draft[k]); });
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var k = keys_1[_i];
            if (Filters_1.isRangeFacet(draft[k])) {
                draft[k].filters.max = null;
                draft[k].filters.min = null;
            }
            else {
                var checkboxes = draft[k].filters.map(function (cb) { return (__assign(__assign({}, cb), { checked: false })); });
                draft[k].filters = checkboxes;
            }
        }
    });
    return next;
}
function generatePreQueryString(facets, activeFilters) {
    if (activeFilters.length === 0) {
        return null;
    }
    var array = Object.keys(facets).map(function (key) { return facets[key]; });
    var onlyActiveFacets = array
        .filter(function (f) { return !isUndefined_1.isUndefined(f); })
        .filter(function (f) { return activeFilters.some(function (af) { return af.parentId === f.id; }); });
    var stringified = onlyActiveFacets
        .map(function (f, i) {
        if (Filters_1.isRangeFacet(f)) {
            var _a = f.filters, min = _a.min, max = _a.max;
            return "" + (i !== 0 ? ' AND ' : '') + f.id.toString() + ":[" + (isNull_1.isNull(min) ? '*' : min) + " TO " + (isNull_1.isNull(max) ? '*' : max) + "]";
        }
        if (Filters_1.isCheckboxesFacet(f)) {
            var checkedBoxes = f.filters.filter(function (checkbox) { return checkbox.checked; });
            var queryValues = checkedBoxes.map(function (cb) { return "" + cb.label; });
            if (queryValues.length === 0) {
                return '';
            }
            return "" + (i !== 0 ? ' AND ' : '') + f.id.toString() + ":(" + queryValues.reduce(function (prev, curr) { return "(" + prev + ") OR (" + curr + ")"; }) + ")";
        }
    })
        .reduce(function (prev, curr) { return "" + prev + curr; }, '');
    return "" + stringified;
}
function determinePostFilterNames(activeFilters) {
    if (activeFilters.length === 0) {
        return [];
    }
    var setOfFacetsWithActiveFilters = new Set(activeFilters.map(function (af) { return af.parentId; }));
    return Array.from(setOfFacetsWithActiveFilters).map(function (facet) { return facet; });
}
// eslint-disable-next-line consistent-return
function reducer(state, action) {
    switch (action.type) {
        case 'initialise': {
            var _a = action.payload, searchTerm = _a.searchTerm, preQueryString = _a.preQueryString;
            return {
                searchTerm: searchTerm,
                queryString: searchTerm,
                facets: {},
                activeFilters: [],
                postFilters: [],
                preQueryString: preQueryString || null,
            };
        }
        case 'toggleFilter': {
            var facets = updateFacets(state.facets, action.payload);
            var activeFilters = updateActiveFilters(state.activeFilters, action.payload);
            var preQueryString = generatePreQueryString(facets, activeFilters);
            var postFilters = determinePostFilterNames(activeFilters);
            return __assign(__assign({}, state), { activeFilters: activeFilters,
                facets: facets,
                postFilters: postFilters,
                preQueryString: preQueryString, queryString: state.searchTerm });
        }
        case 'clearAllActiveFilters': {
            return __assign(__assign({}, state), { facets: clearFilters(state.facets), queryString: state.searchTerm, activeFilters: [], postFilters: [], preQueryString: null });
        }
        case 'mergeFacets': {
            return __assign(__assign({}, state), { facets: mergeFacets(action.payload, state.activeFilters) });
        }
        default: {
            return state;
        }
    }
}
function useFilters() {
    var initialState = {
        queryString: '',
        searchTerm: '',
        facets: {},
        activeFilters: [],
        preQueryString: null,
        postFilters: [],
    };
    var _a = React.useReducer(reducer, initialState, function () { return initialState; }), state = _a[0], dispatch = _a[1];
    return { state: state, dispatch: dispatch };
}
exports.default = useFilters;
